import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import Card from '../../components/common/Card';

interface Stats {
  pendingRequests: number;
  activeConstructions: number;
  todayTasks: number;
  unreadNotifications: number;
}

interface RecentRequest {
  id: string;
  requestNumber: string;
  companyName: string;
  status: string;
}

interface ActiveConstruction {
  id: string;
  constructionNumber: string;
  title: string;
  status: string;
}

interface Task {
  id: string;
  title: string;
  status: string;
}

interface Announcement {
  id: string;
  title: string;
  createdAt: string;
}

interface Mention {
  id: string;
  content: string;
  senderName: string;
  sourceType: string;
  sourceId: string;
  sourceName: string;
  createdAt: string;
}

interface UpcomingEvent {
  id: string;
  title: string;
  startDate: string;
  eventType: string;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<Stats>({
    pendingRequests: 0,
    activeConstructions: 0,
    todayTasks: 0,
    unreadNotifications: 0,
  });
  const [recentRequests, setRecentRequests] = useState<RecentRequest[]>([]);
  const [activeConstructions, setActiveConstructions] = useState<ActiveConstruction[]>([]);
  const [todayTasks, setTodayTasks] = useState<Task[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // 権限チェック関数
  const hasPermission = (permission: string): boolean => {
    if (!user?.permissions) return false;
    return user.permissions.includes(permission);
  };

  useEffect(() => {
    if (user) fetchDashboardData();
  }, [user?.id]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // 並列でAPI呼び出し
      const [statsData, requestsData, constructionsData, tasksData, announcementsData, mentionsData, eventsData] = await Promise.all([
        api.get<Stats>('/api/dashboard/stats').catch(() => ({ pendingRequests: 0, activeConstructions: 0, todayTasks: 0, unreadNotifications: 0 })),
        api.get<{ items: RecentRequest[] }>('/api/dashboard/recent-requests').catch(() => ({ items: [] })),
        api.get<{ items: ActiveConstruction[] }>('/api/dashboard/active-constructions').catch(() => ({ items: [] })),
        api.get<{ items: Task[] }>('/api/dashboard/today-tasks').catch(() => ({ items: [] })),
        api.get<{ items: Announcement[] }>('/api/dashboard/announcements').catch(() => ({ items: [] })),
        api.get<{ items: Mention[] }>('/api/dashboard/mentions').catch(() => ({ items: [] })),
        api.get<{ items: UpcomingEvent[] }>('/api/dashboard/upcoming-events').catch(() => ({ items: [] })),
      ]);

      setStats(statsData ?? { pendingRequests: 0, activeConstructions: 0, todayTasks: 0, unreadNotifications: 0 });
      setRecentRequests(Array.isArray(requestsData?.items) ? requestsData.items : []);
      setActiveConstructions(Array.isArray(constructionsData?.items) ? constructionsData.items : []);
      setTodayTasks(Array.isArray(tasksData?.items) ? tasksData.items : []);
      setAnnouncements(Array.isArray(announcementsData?.items) ? announcementsData.items : []);
      setMentions(Array.isArray(mentionsData?.items) ? mentionsData.items : []);
      setUpcomingEvents(Array.isArray(eventsData?.items) ? eventsData.items : []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 権限に基づいて統計カードをフィルタリング
  const allStatCards = [
    { label: '対応待ち要請', value: stats.pendingRequests, color: 'bg-yellow-500', link: '/requests?status=pending', permission: 'requests.view' },
    { label: '進行中工事', value: stats.activeConstructions, color: 'bg-blue-500', link: '/constructions?status=in_progress', permission: 'constructions.view' },
    { label: '本日のタスク', value: stats.todayTasks, color: 'bg-green-500', link: '/tasks', permission: 'tasks.view' },
    { label: '未読通知', value: stats.unreadNotifications, color: 'bg-red-500', link: '/notifications', permission: null },
  ];
  const statCards = allStatCards.filter(stat => !stat.permission || hasPermission(stat.permission));

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: '対応待ち',
      in_progress: '進行中',
      completed: '完了',
      cancelled: 'キャンセル',
      todo: '未着手',
      done: '完了',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-gray-100 text-gray-700',
      todo: 'bg-gray-100 text-gray-700',
      done: 'bg-green-100 text-green-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ウェルカムメッセージ */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 text-white">
        <h1 className="text-xl sm:text-2xl font-bold mb-1">おかえりなさい、{user?.name}さん</h1>
        <p className="text-blue-100 text-sm sm:text-base">今日も良い一日を！</p>
      </div>

      {/* クイックアクション */}
      <div className="flex flex-wrap gap-3">
        {hasPermission('daily_reports.create') && (
          <Link
            to="/daily-reports?action=new"
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            日報を作成
          </Link>
        )}
        {hasPermission('requests.create') && (
          <Link
            to="/requests?action=new"
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            新規要請
          </Link>
        )}
        {hasPermission('tasks.create') && (
          <Link
            to="/tasks?action=new"
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            タスク追加
          </Link>
        )}
        {hasPermission('calendar.view') && (
          <Link
            to="/calendar"
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            カレンダー
          </Link>
        )}
      </div>

      {/* 統計カード */}
      {statCards.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {statCards.map((stat, index) => (
            <Link
              key={index}
              to={stat.link}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">{stat.label}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-1">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 ${stat.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <span className="text-white text-lg sm:text-xl font-bold">{stat.value}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* メインコンテンツ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* 最近の要請 */}
        {hasPermission('requests.view') && (
          <Card title="最近の要請" actions={<Link to="/requests" className="text-sm text-blue-600 hover:underline">すべて見る</Link>}>
            <div className="space-y-3">
              {recentRequests.length > 0 ? (
                recentRequests.map((request) => (
                  <Link
                    key={request.id}
                    to={`/requests/${request.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="min-w-0 flex-1 mr-2">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{request.requestNumber}</p>
                      <p className="text-sm text-gray-500 truncate">{request.companyName || '-'}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatusColor(request.status)}`}>
                      {getStatusLabel(request.status)}
                    </span>
                  </Link>
                ))
              ) : (
                <p className="text-center text-gray-500 py-4">要請がありません</p>
              )}
            </div>
          </Card>
        )}

        {/* 進行中の工事 */}
        {hasPermission('constructions.view') && (
          <Card title="進行中の工事" actions={<Link to="/constructions" className="text-sm text-blue-600 hover:underline">すべて見る</Link>}>
            <div className="space-y-3">
              {activeConstructions.length > 0 ? (
                activeConstructions.map((construction) => (
                  <Link
                    key={construction.id}
                    to={`/constructions/${construction.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="min-w-0 flex-1 mr-2">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{construction.constructionNumber}</p>
                      <p className="text-sm text-gray-500 truncate">{construction.title}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatusColor(construction.status)}`}>
                      {getStatusLabel(construction.status)}
                    </span>
                  </Link>
                ))
              ) : (
                <p className="text-center text-gray-500 py-4">進行中の工事がありません</p>
              )}
            </div>
          </Card>
        )}

        {/* 今日のタスク */}
        {hasPermission('tasks.view') && (
          <Card title="今日のタスク" actions={<Link to="/tasks" className="text-sm text-blue-600 hover:underline">すべて見る</Link>}>
            <div className="space-y-2">
              {todayTasks.length > 0 ? (
                todayTasks.map((task) => (
                  <div key={task.id} className="flex items-center gap-3 p-2">
                    <input
                      type="checkbox"
                      checked={task.status === 'done'}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0 cursor-pointer"
                      onChange={async () => {
                        const newStatus = task.status === 'done' ? 'pending' : 'done';
                        try {
                          await api.put(`/api/tasks/${task.id}`, { status: newStatus });
                          setTodayTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
                        } catch (error) {
                          console.error('Failed to update task:', error);
                        }
                      }}
                    />
                    <span className={`${task.status === 'done' ? 'line-through text-gray-400' : 'text-gray-900 dark:text-white'} truncate`}>
                      {task.title}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-4">タスクがありません</p>
              )}
            </div>
          </Card>
        )}

        {/* お知らせ */}
        {hasPermission('board.view') && (
          <Card title="お知らせ" actions={<Link to="/board" className="text-sm text-blue-600 hover:underline">すべて見る</Link>}>
            <div className="space-y-3">
              {announcements.length > 0 ? (
                announcements.map((announcement) => (
                  <Link
                    key={announcement.id}
                    to={`/board/${announcement.id}`}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded transition-colors"
                  >
                    <span className="text-gray-900 dark:text-white truncate flex-1 mr-2">{announcement.title}</span>
                    <span className="text-sm text-gray-500 whitespace-nowrap">{formatDate(announcement.createdAt)}</span>
                  </Link>
                ))
              ) : (
                <p className="text-center text-gray-500 py-4">お知らせがありません</p>
              )}
            </div>
          </Card>
        )}

        {/* メンション一覧 */}
        <Card title="あなた宛てのメンション" actions={<span className="text-sm text-blue-600">💬</span>}>
          <div className="space-y-3">
            {mentions.length > 0 ? (
              mentions.map((mention) => (
                <Link
                  key={mention.id}
                  to={`/${mention.sourceType}/${mention.sourceId}`}
                  className="block p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-blue-500">@</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded">
                          {mention.sourceName}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900 dark:text-white line-clamp-2">{mention.content}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>{mention.senderName}</span>
                        <span>·</span>
                        <span>{formatDate(mention.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <p className="text-center text-gray-500 py-4">メンションはありません</p>
            )}
          </div>
        </Card>

        {/* 今後の予定 */}
        {hasPermission('calendar.view') && (
          <Card title="今後3日間の予定" actions={<Link to="/calendar" className="text-sm text-blue-600 hover:underline">カレンダー</Link>}>
            <div className="space-y-3">
              {upcomingEvents.length > 0 ? (
                upcomingEvents.map((event) => {
                  const eventDate = new Date(event.startDate);
                  const isToday = eventDate.toDateString() === new Date().toDateString();
                  const isTomorrow = eventDate.toDateString() === new Date(Date.now() + 86400000).toDateString();
                  const dayLabel = isToday ? '今日' : isTomorrow ? '明日' : eventDate.toLocaleDateString('ja-JP', { weekday: 'short', month: 'short', day: 'numeric' });
                  
                  return (
                    <div
                      key={event.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                    >
                      <div className={`w-3 h-3 rounded-full ${
                        event.eventType === 'meeting' ? 'bg-blue-500' :
                        event.eventType === 'deadline' ? 'bg-red-500' :
                        event.eventType === 'construction' ? 'bg-orange-500' : 'bg-gray-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{event.title}</p>
                        <p className="text-xs text-gray-500">
                          {dayLabel} {eventDate.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-gray-500 py-4">予定はありません</p>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
